import type { RefObject } from 'react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTracking } from 'react-tracking';

import { Button } from '@simplywallst/ui-core/next';
import useUser from '@/hooks/useUser';
import { useExistingNarrative } from '@/hooks/narrative/useExistingNarrative';
import GuestRegistrationModal from '@components/GuestRegistrationModal';
import CheckSolid from '@simplywallst/ui-core/icons/CheckSolid';
import { useNarrativeInvalidate } from '@/pages/Narrative/hooks/useNarrativeInvalidate';
import { useNarrativeSelectValuationOption } from '@/hooks/narrative/useNarrativeSelectValuationOption';
import { FairValueTooltip } from '@/pages/Narrative/components/FairValueTooltip';
import { NarrativePanelRegister } from '@/components/GuestRegistrationModal/components/NarrativePanelRegister';

type Props = {
  companyId: string;
  narrativeId: string;
  containerRef: RefObject<HTMLDivElement>;
};

export const SetFairValue = ({
  companyId,
  narrativeId,
  containerRef,
}: Props) => {
  const [isSettingFairValue, setIsSettingFairValue] = useState(false);
  const { data: user, mutate: mutateUser } = useUser();
  const { data: existingNarrative, status } = useExistingNarrative({
    companyId: companyId.toLowerCase(),
    loggedIn: user.loggedIn,
  });
  const [isGuestRegistrationModalOpen, setGuestRegistrationModal] =
    useState(false);
  const { mutateAsync: setFairValue } = useNarrativeSelectValuationOption();
  const queryClient = useQueryClient();
  const { invalidate } = useNarrativeInvalidate();
  const { trackEvent } = useTracking();

  const showSetFairValueTooltip = !user.narrative?.hasDismissedFairValueTooltip;

  const handleOnDismissTooltip = () => {
    trackEvent({
      subject: 'set_fair_value_tooltip',
      action: 'dismiss',
      companyId,
      narrativeId,
    });

    mutateUser({
      settings: {
        narrative: {
          has_dismissed_fair_value_tooltip: true,
        },
      },
    });
  };

  if (!user.loggedIn) {
    return (
      <>
        <Button
          size="medium"
          variant="primary"
          onClick={() => setGuestRegistrationModal(true)}
        >
          Set as Fair Value
        </Button>
        {isGuestRegistrationModalOpen && (
          <GuestRegistrationModal
            canClose
            onClose={() => setGuestRegistrationModal(false)}
            removeWindowScroll
            onLoginSuccess={() => setGuestRegistrationModal(false)}
            onRegisterSuccess={() => setGuestRegistrationModal(false)}
            SplashPanel={NarrativePanelRegister}
          />
        )}
      </>
    );
  }

  const handleOnClick = async () => {
    if (isPrimaryNarrative) return;

    setIsSettingFairValue(true);

    try {
      await setFairValue({
        input: { companyId, narrativeId, type: 'USER_DEFINED' },
      });

      trackEvent({
        action: 'save',
        companyId,
        valuationType: 'USER_DEFINED',
        narrativeId,
      });

      handleOnDismissTooltip();

      await queryClient.refetchQueries(
        useExistingNarrative.getOptions({
          companyId: companyId.toLowerCase(),
          loggedIn: user.loggedIn,
        })
      );

      invalidate();
    } catch (error) {
      console.error('Unable to set narrative as primary', error);
    }

    setIsSettingFairValue(false);
  };

  if (status === 'error' || !existingNarrative) {
    return null;
  }

  if (!companyId || !narrativeId) {
    return null;
  }

  const { narrative } = existingNarrative.Company.myValuation || {};
  const isPrimaryNarrative = narrative && narrative.id === narrativeId;

  return (
    <FairValueTooltip
      show={showSetFairValueTooltip && !isPrimaryNarrative}
      onClick={handleOnDismissTooltip}
      containerRef={containerRef}
    >
      <Button
        isLoading={isSettingFairValue}
        isDisabled={isSettingFairValue}
        size="medium"
        className={isPrimaryNarrative ? 'gap-0 pl-1' : ''}
        variant={isPrimaryNarrative ? 'secondary' : 'primary'}
        onClick={handleOnClick}
      >
        {isPrimaryNarrative ? (
          <>
            <CheckSolid />
            &nbsp;Fair Value
          </>
        ) : (
          'Set as Fair Value'
        )}
      </Button>
    </FairValueTooltip>
  );
};
